import { database } from '../../firebase';
import { ref, get, set, push, increment } from 'firebase/database';

/**
 * Get admin stats from Firebase Realtime Database
 */
export const getAdminStats = async () => {
  try {
    // Get admin stats from root/admin
    const adminRef = ref(database, 'admin');
    const adminSnapshot = await get(adminRef);
    const adminData = adminSnapshot.val() || {};

    // Get wishlist count from root/wishlist
    const wishlistRef = ref(database, 'wishlist');
    const wishlistSnapshot = await get(wishlistRef);
    const wishlistData = wishlistSnapshot.val() || {};
    const wishlistCount = Object.keys(wishlistData).length;

    // Get email stats
    const emailStats = adminData.emailStats || {};
    const totalEmailsSent = emailStats.totalEmailsSent || 0;
    const lastCampaignDate = emailStats.lastCampaignDate || null;

    // Get user stats (if available)
    const userStats = adminData.userStats || {};
    const totalUsers = userStats.totalUsers || 0;

    // Calculate some derived metrics
    const conversionRate = wishlistCount > 0 && totalUsers > 0 
      ? ((wishlistCount / totalUsers) * 100).toFixed(1) 
      : 0;

    return {
      success: true,
      data: {
        totalUsers,
        wishlistEntries: wishlistCount,
        emailsSent: totalEmailsSent,
        conversionRate: parseFloat(conversionRate),
        recentActivity: wishlistCount, // Using wishlist count as proxy for recent activity
        systemHealth: 98.5, // This could be calculated based on various factors
        lastCampaignDate
      }
    };
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    return {
      success: false,
      error: error.message,
      data: {
        totalUsers: 0,
        wishlistEntries: 0,
        emailsSent: 0,
        conversionRate: 0,
        recentActivity: 0,
        systemHealth: 98.5,
        lastCampaignDate: null
      }
    };
  }
};

/**
 * Update email stats when a campaign is sent
 */
export const updateEmailStats = async (emailsSentCount, campaignData = {}) => {
  try {
    const adminRef = ref(database, 'admin/emailStats');
    
    // Get current stats
    const snapshot = await get(adminRef);
    const currentStats = snapshot.val() || {};
    
    // Update stats
    const updatedStats = {
      totalEmailsSent: (currentStats.totalEmailsSent || 0) + emailsSentCount,
      lastCampaignDate: new Date().toISOString(),
      lastCampaignSubject: campaignData.subject || '',
      lastCampaignRecipients: emailsSentCount,
      campaignHistory: {
        ...currentStats.campaignHistory,
        [Date.now()]: {
          date: new Date().toISOString(),
          subject: campaignData.subject || '',
          recipients: emailsSentCount,
          playStoreLink: campaignData.playStoreLink || '',
          appStoreLink: campaignData.appStoreLink || ''
        }
      }
    };

    await set(adminRef, updatedStats);
    
    return {
      success: true,
      data: updatedStats
    };
  } catch (error) {
    console.error('Error updating email stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user stats (can be called when new users register)
 */
export const updateUserStats = async (userCount) => {
  try {
    const userStatsRef = ref(database, 'admin/userStats');
    
    const updatedStats = {
      totalUsers: userCount,
      lastUpdated: new Date().toISOString()
    };

    await set(userStatsRef, updatedStats);
    
    return {
      success: true,
      data: updatedStats
    };
  } catch (error) {
    console.error('Error updating user stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Initialize admin stats structure in Firebase (one-time setup)
 */
export const initializeAdminStats = async () => {
  try {
    const adminRef = ref(database, 'admin');
    const snapshot = await get(adminRef);
    
    if (!snapshot.exists()) {
      const initialStats = {
        emailStats: {
          totalEmailsSent: 0,
          lastCampaignDate: null,
          campaignHistory: {}
        },
        userStats: {
          totalUsers: 0,
          lastUpdated: new Date().toISOString()
        },
        createdAt: new Date().toISOString()
      };

      await set(adminRef, initialStats);
      
      return {
        success: true,
        message: 'Admin stats initialized successfully',
        data: initialStats
      };
    }
    
    return {
      success: true,
      message: 'Admin stats already exist',
      data: snapshot.val()
    };
  } catch (error) {
    console.error('Error initializing admin stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get campaign history
 */
export const getCampaignHistory = async () => {
  try {
    const campaignRef = ref(database, 'admin/emailStats/campaignHistory');
    const snapshot = await get(campaignRef);
    const campaignHistory = snapshot.val() || {};
    
    // Convert to array and sort by date (newest first)
    const campaigns = Object.entries(campaignHistory)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return {
      success: true,
      data: campaigns
    };
  } catch (error) {
    console.error('Error fetching campaign history:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Get recent activities from Firebase (real data only)
 */
export const getRecentActivities = async (limit = 10) => {
  try {
    const activities = [];

    // Get recent wishlist entries
    const wishlistRef = ref(database, 'wishlist');
    const wishlistSnapshot = await get(wishlistRef);
    const wishlistData = wishlistSnapshot.val() || {};
    
    Object.entries(wishlistData).forEach(([id, entry]) => {
      if (entry.timestamp) {
        activities.push({
          id: `wishlist_${id}`,
          action: 'New wishlist entry',
          user: entry.email,
          time: entry.timestamp,
          type: 'wishlist',
          data: entry
        });
      }
    });

    // Get recent email campaigns
    const campaignRef = ref(database, 'admin/emailStats/campaignHistory');
    const campaignSnapshot = await get(campaignRef);
    const campaignHistory = campaignSnapshot.val() || {};
    
    Object.entries(campaignHistory).forEach(([id, campaign]) => {
      activities.push({
        id: `campaign_${id}`,
        action: 'Email campaign sent',
        details: `${campaign.recipients} recipients`,
        time: campaign.date,
        type: 'email',
        data: campaign
      });
    });

    // Get admin activity logs (if they exist)
    const adminLogsRef = ref(database, 'admin/activityLogs');
    const adminLogsSnapshot = await get(adminLogsRef);
    const adminLogs = adminLogsSnapshot.val() || {};
    
    Object.entries(adminLogs).forEach(([id, log]) => {
      activities.push({
        id: `admin_${id}`,
        action: log.action || 'System activity',
        user: log.user || log.email,
        time: log.timestamp || log.date,
        type: log.type || 'system',
        data: log
      });
    });

    // Sort all activities by time (newest first) and limit results
    const sortedActivities = activities
      .filter(activity => activity.time) // Only include activities with timestamps
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, limit)
      .map(activity => ({
        ...activity,
        timeAgo: getTimeAgo(activity.time)
      }));

    return {
      success: true,
      data: sortedActivities
    };
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Log admin activity for tracking
 */
export const logAdminActivity = async (activityData) => {
  try {
    const adminLogsRef = ref(database, 'admin/activityLogs');
    const newLogRef = push(adminLogsRef);
    
    const logEntry = {
      ...activityData,
      timestamp: new Date().toISOString(),
      id: newLogRef.key
    };

    await set(newLogRef, logEntry);
    
    return {
      success: true,
      data: logEntry
    };
  } catch (error) {
    console.error('Error logging admin activity:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Helper function to calculate time ago
 */
const getTimeAgo = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInMs = now - time;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  } else {
    return time.toLocaleDateString();
  }
}; 